<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="general content-wrapper">
      <div class="page-header">{{ $t('general') }}</div>
      <div style="margin-bottom: 40px; margin-left: 5px;">
        <el-breadcrumb separator="/">
          <el-breadcrumb-item :to="{ name: 'SettingGeneral' }">{{ $t('general') }}</el-breadcrumb-item>
          <el-breadcrumb-item>{{ $t('editGeneral') }}</el-breadcrumb-item>
        </el-breadcrumb>
      </div>
      <div>
        <el-container>
          <el-main>
            <el-form ref="form"  label-width="150px">
              <el-form-item :label="$t('logo')">
                <el-col :span="24">
                  <div class="image-preview">
                    <img v-if="imageData !== ''" class="preview" width="150px" :src="imageData">
                    <img v-if="imageData === ''" class="preview" width="150px" src="@/assets/logo_placeholder.png">
                  </div>
                  <div>
                    <input-file-component v-model="fileData"
                                          :max-file-size="maxFileSize"
                                          accept="image/*"
                                          @onErrorOverLimitFileSize="onErrorOverLimitFileSize()">
                    </input-file-component>
                  </div>
                  <div><small class="text-remark">{{ $t('remarkImageSize') }}</small></div>
                </el-col>
              </el-form-item>
              <el-form-item :label="$t('currency')">
                <el-select v-model="currency">
                  <el-option v-for="currency in currencyList" :key="currency" :label="currency" :value="currency"></el-option>
                </el-select>
              </el-form-item>

              <div style="border-bottom: 1px solid #eeeeee; padding: 10px"></div>

              <h3>{{ $t('contract') }}</h3>
              <el-form-item :label="$t('refCodeFormat')">
                <el-row>
                  <el-col :span="12">
                    <el-input v-model="contractRefCodeFormat"></el-input>
                  </el-col>
                  <el-col :span="4">
                    <ref-code-formatting-help-component
                      :is-contract-ref-code="true"
                      :is-special-offer-ref-code="false"></ref-code-formatting-help-component>
                  </el-col>
                </el-row>
              </el-form-item>

              <div style="border-bottom: 1px solid #eeeeee; padding: 10px"></div>

              <h3>{{ $t('specialOffer') }}</h3>
              <el-form-item :label="$t('refCodeFormat')">
                <el-row>
                  <el-col :span="12">
                    <el-input v-model="specialOfferRefCodeFormat"></el-input>
                  </el-col>
                  <el-col :span="4">
                    <ref-code-formatting-help-component
                      :is-contract-ref-code="false"
                      :is-special-offer-ref-code="true"></ref-code-formatting-help-component>
                  </el-col>
                </el-row>
              </el-form-item>

              <el-form-item label="">
                <el-button type="primary" @click="onSaveButtonClick">{{ $t('saveChange') }}</el-button>
                <el-button type="default" @click="onCancelButtonClick">{{ $t('cancel') }}</el-button>
              </el-form-item>
            </el-form>
          </el-main>
        </el-container>
      </div>
    </div>
  </ContentContainer>
</template>

<script>
import i18nMessages from '@/translations/Setting/ManageSetting/General/Edit.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import SettingApi from '@/api/setting-api'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import RefCodeFormattingHelpComponent from '@/views/Setting/ManageSetting/General/components/RefCodeFormattingHelpComponent'
import { STATUS } from '@/configs/constant'
import { get } from 'lodash'
import InputFileComponent from '@/components/InputFileComponent'

export default {
  name: 'SettingGeneral',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { ContentContainer, RefCodeFormattingHelpComponent, InputFileComponent },
  i18n: { messages: i18nMessages },
  props: {
    companyDetail: {
      type: Object,
      default: function () {
        return {
          id: '',
          name: '',
          code: '',
          logoUrl: '',
          currency: 'THB',
          contract: {
            refCodeFormat: ''
          },
          specialOffer: {
            refCodeFormat: ''
          },
          companyType: 0,
          isEnable: STATUS.ENABLE
        }
      }
    }
  },
  created: function () {
    if (this.companyDetail === undefined || this.companyDetail.id === '') {
      return this.goToGeneralPage()
    }

    this.imageData = get(this.companyDetail, 'logoUrl', '')
    this.currency = get(this.companyDetail, 'currency', 'THB')
    this.contractRefCodeFormat = get(this.companyDetail, 'contract.refCodeFormat', '')
    this.specialOfferRefCodeFormat = get(this.companyDetail, 'specialOffer.refCodeFormat', '')

    this.isLoading = false
  },
  data () {
    return {
      isLoading: false,
      currency: 'THB',
      contractRefCodeFormat: '',
      specialOfferRefCodeFormat: '',
      currencyList: ['THB', 'USD'],
      fileData: {},
      imageData: '',
      maxFileSize: 5767168
    }
  },
  watch: {
    fileData: function (newVal) {
      this.imageData = get(newVal, 'content', '')
    }
  },
  methods: {
    onSaveButtonClick: async function () {
      try {
        this.startLoading()

        let settingDetail = {
          currency: this.currency,
          contract: {
            refCodeFormat: this.contractRefCodeFormat
          },
          specialOffer: {
            refCodeFormat: this.specialOfferRefCodeFormat
          }
        }

        if (this.imageData !== '' && this.imageData !== this.companyDetail.logoUrl) {
          settingDetail.imageData = this.imageData
        }

        await SettingApi.update(settingDetail)
        const title = this.$t('saveSettingSuccessTitle')
        const message = this.$t('saveSettingSuccessMessage')

        this.showNotificationSuccess(title, message)

        this.goToGeneralPage()
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    onCancelButtonClick: function () {
      return this.goToGeneralPage()
    },
    goToGeneralPage: function () {
      return this.$router.push({
        name: 'SettingGeneral'
      })
    },
    onErrorOverLimitFileSize: function () {
      this.alertError(this.$t('imageSizeOverMessage'))

      return false
    }
  }
}
</script>

<style scoped>
  .text-remark {
    color: #F56C6C;
  }
</style>
